import React, { useState, useEffect, useContext } from 'react';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../../utils/secure-route/AuthContext';

const isValidHexColor = (color) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color || '');

const normalizeColor = (color) => {
  const colorMap = {
    Red: "#EE6055",
    Blue: "#83D3F2",
    Green: "#ADF7B6",
    Lavender: "#BDB2FF",
    Peach: "#FFC89A",
  };
  return colorMap[color] || color || '#83D3F2';
};

const ProductModal = ({ open, onClose, productData, blockIndex, onSave, onSaveSection }) => {
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    color: '#83D3F2',
    section: ''
  });
  const { Post, Patch } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;

  useEffect(() => {
    if (productData?.isSet) {
      setFormData({
        title: productData.title || '',
        price: productData.price || '',
        color: normalizeColor(productData.color),
        section: productData.section || ''
      });
    } else {
      setFormData({ title: '', price: '', color: '#83D3F2', section: '' });
    }
  }, [productData, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const numericPrice = parseFloat(formData.price);
    if (Number.isNaN(numericPrice) || numericPrice === 0) {
      window.alert('Please enter a non-zero price. Negative values are allowed.');
      return;
    }
    if (!isValidHexColor(formData.color)) {
      window.alert('Please select a valid card color.');
      return;
    }
    if (!window.confirm(productData?.id ? 'Are you sure you want to update this product?' : 'Are you sure you want to add this product?')) {
      return;
    }
    try {
      const payload = {
        store: storeId,
        title: formData.title,
        price: numericPrice,
        color: formData.color,
        index: blockIndex
      };

      if (productData?.id) {
        await Patch('posItem', productData.id, payload);
      } else {
        await Post('posItem', payload);
      }

      if (typeof onSaveSection === 'function') {
        onSaveSection(blockIndex, formData.section, productData?.id);
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  if (!open) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {productData?.isSet ? 'Edit Product' : 'Add Product'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label fw-semibold">Product Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Price</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
                <div className="form-text">Use a negative value for discounts, returns, or adjustments.</div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Color</label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={isValidHexColor(formData.color) ? formData.color : '#83D3F2'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    title="Choose card color"
                  />
                  <input
                    type="text"
                    className="form-control"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                    placeholder="#83D3F2"
                    required
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Section</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.section}
                  onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                  placeholder="Optional, e.g. Snacks or Discounts"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
